import { Transition } from '@headlessui/react';
import { type FC, type ReactNode, useState } from 'react';

import { twMergeClsx } from 'utils/tw-with-clsx';

enum TooltipPosition {
  TOP = 'top',
  LEFT = 'left',
  RIGHT = 'right',
  BOTTOM = 'bottom',
}

type TooltipPropTypes = {
  children: ReactNode;
  text: string;
  className?: string;
  position?: TooltipPosition;
};

const Tooltip: FC<TooltipPropTypes> = ({
  children,
  text,
  className,
  position = TooltipPosition.TOP,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <button
      type="button"
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <Transition
        show={showTooltip}
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div
          className={twMergeClsx(
            'absolute w-48 border transition ease-in-out',
            'transition duration-300 data-[closed]:opacity-0 z-50',
            position === TooltipPosition.TOP && 'bottom-full left-1/2 -translate-x-1/2',
            position === TooltipPosition.BOTTOM && 'top-full left-1/2 -translate-x-1/2',
            position === TooltipPosition.LEFT && 'right-full top-1/2 -translate-y-1/2',
            position === TooltipPosition.RIGHT && 'left-full top-1/2 -translate-y-1/2',
            className,
          )}
        >
          {text}
        </div>
      </Transition>
    </button>
  );
};

export { Tooltip, TooltipPosition };
